import { SearchResultData } from "../app/slices/SearchResultsSlice";
import { InteractionFactoryEntity } from "../features/interaction_factory/InteractionFactory";
import { AnimateActorPropertiesEditor } from "../features/interaction_properties_column/editors/AnimateActorPropertiesEditor";
import AnimateActorPreview from "../features/previews/animate_actor/AnimateActorPreview";
import AnimateActorNodePreview from "../features/sequence/node_previews/AnimateActorNodePreview";
import { GetNewGuid } from "../helpers/GuidHelper";
import { Actor } from "./Actor";
import Interaction, { InteractionType } from "./interactions/Interaction";
import {
  compactSearchableStrings,
  SearchLookupContext,
} from "./interactions/SearchableInteractionValues";
import Take from "./Take";

export interface AnimateActorAnimation {
  name: string;
  animationState: string;
}

export type StoredAnimateActorAnimation = AnimateActorAnimation | string;

export const defaultAnimateActorAnimation: AnimateActorAnimation = {
  name: "",
  animationState: "",
};

export function getAnimateActorAnimationName(
  animation: StoredAnimateActorAnimation | undefined
): string {
  return typeof animation === "string" ? animation : animation?.name ?? "";
}

export function getAnimateActorAnimationState(
  animation: StoredAnimateActorAnimation | undefined
): string {
  return typeof animation === "string"
    ? animation
    : animation?.animationState || animation?.name || "";
}

export interface IAnimateActor extends Interaction {
  npcId: string;
  animation: StoredAnimateActorAnimation;
  layer: number;
  transitionTime: number;
  waitUntilFinished: boolean;
}

export const animateActorInteraction = (): InteractionFactoryEntity => {
  const view = <AnimateActorPreview />;
  const create = (getKeys: (amount: number) => string[]): IAnimateActor => {
    return {
      id: GetNewGuid(),
      name: "Animate Actor",
      type: InteractionType.AnimateActor,
      npcId: "player",
      animation: { ...defaultAnimateActorAnimation },
      layer: 0,
      transitionTime: 0.3,
      waitUntilFinished: false,
    };
  };

  const migrate = (
    from: number,
    to: number,
    interaction: Interaction
  ): IAnimateActor => {
    return interaction as IAnimateActor;
  };

  const validate = (
    interaction: Interaction,
    getValue: (key: string) => string
  ): string[] => {
    return [];
  };

  const copy = (
    interaction: Interaction,
    getKeys: (amount: number) => string[],
    replaceKey: (from: string, to: string) => void
  ): IAnimateActor => {
    return {
      ...(interaction as IAnimateActor),
      id: GetNewGuid(),
    };
  };

  const filter = (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string
    ) => SearchResultData[],
    _getValue: (key: string) => string,
    searchLookupContext?: SearchLookupContext,
  ): SearchResultData[] => {
    const casted = interaction as IAnimateActor;

    return getMatchingResults(
      compactSearchableStrings(
        interaction.name,
        searchLookupContext?.getActorSearchTerms(casted.npcId) ?? casted.npcId,
        getAnimateActorAnimationName(casted.animation),
        getAnimateActorAnimationState(casted.animation),
      ),
      takeName,
      interaction,
      "Animate Actor",
    );
  };

  const validateErrors = (
    interaction: Interaction,
    parentTake: Take,
    currentTake: Take,
    actorPresets: Actor[]
  ): string[] => {
    return [];
  };

  return {
    View: view,
    NodeView: (nodeId: string, interaction: Interaction) => (
      <AnimateActorNodePreview nodeId={nodeId} interaction={interaction} />
    ),
    PropertiesEditor: AnimateActorPropertiesEditor,
    FilterType: "Animate Actor",
    Create: create,
    Migrate: migrate,
    Copy: copy,
    Validate: validate,
    Filter: filter,
    ValidateErrors: validateErrors,
  };
};
